import {
  BookOpen,
  LoaderCircle,
  MessageSquare,
  Plus,
  Settings,
  SlidersHorizontal,
  X,
  Workflow,
} from "lucide-react";
import { Button } from "@/components/ui/shadcn/Button/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/DropdownMenu/DropdownMenu";
import type {
  AiChatThreadHeader,
  AiChatThreadStatus,
} from "@/lib/types/ai-chat";
import { cn } from "@/lib/utils/cn";
import { ChatStatus } from "./ChatStatus";
import { memo } from "react";
import { ChatActivityIndicator } from "@/components/ui/AiChatPanel/ChatActivityIndicator";

interface ChatThreadListProps {
  threads: AiChatThreadHeader[];
  selectedId?: string;
  status?: AiChatThreadStatus;
  onStatusChange: (status?: AiChatThreadStatus) => void;
  onSelect: (id: string) => void;
  onNew: () => void;
  canAccessSkills?: boolean;
  canAccessAutomations?: boolean;
  onSettingsNavigate?: (page: "skills" | "automations") => void;
  onClose?: () => void;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore: () => void;
  error?: string;
  onRetry: () => void;
}

export const ChatThreadList = memo(function ChatThreadList({
  threads,
  selectedId,
  status,
  onStatusChange,
  onSelect,
  onNew,
  canAccessSkills,
  canAccessAutomations,
  onSettingsNavigate,
  onClose,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  error,
  onRetry,
}: ChatThreadListProps) {
  return (
    <aside
      aria-label="AI threads"
      className="bg-muted/35 flex h-full w-full flex-col"
    >
      <div className="flex h-16 shrink-0 items-center justify-between px-5">
        <h2 className="text-sm font-semibold tracking-tight">Your threads</h2>
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="size-8"
            aria-label="New thread"
            title="New thread"
            onClick={onNew}
          >
            <Plus className="size-4" />
          </Button>
          {onSettingsNavigate && (canAccessSkills || canAccessAutomations) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-8"
                  aria-label="AI settings"
                  title="AI settings"
                >
                  <Settings className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canAccessSkills && (
                  <DropdownMenuItem
                    onSelect={() => {
                      onSettingsNavigate("skills");
                    }}
                  >
                    <BookOpen />
                    Skills
                  </DropdownMenuItem>
                )}
                {canAccessAutomations && (
                  <DropdownMenuItem
                    onSelect={() => {
                      onSettingsNavigate("automations");
                    }}
                  >
                    <Workflow />
                    Automations
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {onClose && (
            <Button
              size="icon"
              variant="ghost"
              className="size-8 md:hidden"
              aria-label="Close threads"
              onClick={onClose}
            >
              <X />
            </Button>
          )}
        </div>
      </div>
      <div className="px-4 pb-4">
        <div className="text-muted-foreground border-border/70 relative flex items-center gap-2 rounded-lg border px-3">
          <SlidersHorizontal
            className="pointer-events-none size-3.5 shrink-0"
            aria-hidden="true"
          />
          <select
            aria-label="Filter threads"
            value={status ?? ""}
            onChange={(event) => {
              onStatusChange(
                (event.target.value || undefined) as
                  | AiChatThreadStatus
                  | undefined,
              );
            }}
            className="h-9 min-w-0 flex-1 bg-transparent text-xs outline-none"
          >
            <option value="">All threads</option>
            <option value="pending_approval">Needs approval</option>
            <option value="running">Working</option>
            <option value="completed_unread">New responses</option>
            <option value="failed_unread">Needs attention</option>
          </select>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        {isLoading && (
          <div
            role="status"
            className="text-muted-foreground flex items-center gap-2 p-4 text-xs"
          >
            <LoaderCircle className="size-3.5 animate-spin" />
            Loading threads…
          </div>
        )}
        {error && (
          <div role="alert" className="px-3 py-4 text-sm">
            <p className="text-destructive">{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={onRetry}
            >
              Try again
            </Button>
          </div>
        )}
        {!isLoading && !error && threads.length === 0 && (
          <div className="text-muted-foreground px-4 py-8 text-center text-xs leading-5">
            <MessageSquare className="mx-auto mb-3 size-5 opacity-50" />
            {status
              ? "No threads in this view."
              : "Your conversations will appear here."}
          </div>
        )}
        <ul className="space-y-1">
          {threads.map((thread) => (
            <li key={thread.id}>
              <button
                type="button"
                onClick={() => {
                  onSelect(thread.id);
                }}
                aria-current={thread.id === selectedId ? "page" : undefined}
                className={cn(
                  "group hover:bg-muted flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors",
                  thread.id === selectedId &&
                    "bg-background ring-1 shadow-[0_1px_4px_rgba(0,0,0,0.05)] ring-black/5 dark:ring-white/5",
                )}
              >
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      "block truncate text-[13px] leading-5",
                      [
                        "completed_unread",
                        "failed_unread",
                        "pending_approval",
                      ].includes(thread.status)
                        ? "font-medium"
                        : "text-foreground/85",
                    )}
                  >
                    {thread.title || "New thread"}
                  </span>
                  {["pending_approval", "failed_unread"].includes(
                    thread.status,
                  ) && (
                    <span className="mt-1.5 block">
                      <ChatStatus status={thread.status} />
                    </span>
                  )}
                </span>
                {thread.status === "running" && (
                  <span
                    className="text-muted-foreground pt-1"
                    role="status"
                    aria-label="Working"
                    title="Working"
                  >
                    <ChatActivityIndicator active appearance="spinner" />
                  </span>
                )}
                {thread.status === "completed_unread" && (
                  <span className="pt-1">
                    <ChatStatus status={thread.status} compact />
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
        {hasMore && (
          <Button
            className="mt-3 w-full text-xs"
            variant="ghost"
            disabled={isLoadingMore}
            onClick={onLoadMore}
          >
            {isLoadingMore ? <LoaderCircle className="animate-spin" /> : null}
            Load more threads
          </Button>
        )}
      </div>
    </aside>
  );
});
